// @ds/data/runQuery — Refine hook dispatchers for Query<T> / RecordQuery<T>.
//
// Three runners, picked by SURFACE (not by Query shape):
//
//   - `useRunListQuery`   → `useList`   — passive, no URL sync.
//                           View sub-sections, peek sub-sections.
//   - `useRunTableQuery`  → `useTable`  — Refine owns sort + pagination,
//                                          URL-synced.
//                           Top-level list pages, queue tabs.
//   - `useRunRecordQuery` → `useOne`    — single-row fetch.
//                           View / peek record fetch.
//
// Each runner takes a `Query<T>` (or `RecordQuery<T>`) and an optional
// `layoutFields` array. The effective fetch is:
//   `(query.fields ?? layoutFields ?? []) ∪ query.also ∪ schema.permanentFields`
//
// Dotted-path filters in `query.filter` are auto-translated into `!inner`
// joins via `deriveSelect` → `detectInnerJoins`.

import { useList, useTable, useOne, type BaseRecord } from "@refinedev/core";
import { getSchemaMeta } from "./schema";
import { composeFilters, deriveSelect, deriveSelectFromColumns } from "./queryHelpers";
import { buildSelectString } from "@ds/tables/helpers/buildSelectString";
import type { FieldDef } from "@ds/display/fieldDisplay";
import type { Query, RecordQuery } from "./query";

// ─── useRunListQuery ────────────────────────────────────────────────────────

/**
 * Passive list fetch. Used by view sub-sections, peek sub-sections —
 * surfaces that don't own interaction state and shouldn't pollute the URL.
 */
export function useRunListQuery<T extends BaseRecord = BaseRecord>(
  q: Query<T>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  layoutColumns?: ReadonlyArray<FieldDef<any, any>>,
) {
  const meta = getSchemaMeta(q.schema);
  // Object-based: derive the select from the layout's column OBJECTS' deps
  // (supports config-composed pickRef/col columns). `q.fields` still overrides.
  const select = q.fields
    ? deriveSelect(q.schema, q.fields, q.also, q.filter ?? [])
    : deriveSelectFromColumns(q.schema, layoutColumns ?? [], q.also, q.filter ?? []);
  return useList<T>({
    resource: meta.resource,
    filters: composeFilters(q.schema, q.filter),
    sorters: q.sort,
    pagination: {
      currentPage: q.page?.index ?? 1,
      pageSize:    q.page?.size ?? 50,
    },
    meta: select ? { select } : undefined,
    queryOptions: { enabled: q.enabled ?? true },
  });
}

// ─── useRunTableQuery ───────────────────────────────────────────────────────

/**
 * Interactive table fetch — Refine owns sort + pagination, URL-synced.
 * Used by top-level list pages and queue tabs.
 */
export function useRunTableQuery<T extends BaseRecord = BaseRecord>(
  q: Query<T>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  layoutColumns?: ReadonlyArray<FieldDef<any, any>>,
) {
  const meta = getSchemaMeta(q.schema);
  const select = q.fields
    ? deriveSelect(q.schema, q.fields, q.also, q.filter ?? [])
    : deriveSelectFromColumns(q.schema, layoutColumns ?? [], q.also, q.filter ?? []);
  const clientMode = q.mode === "client";
  return useTable<T>({
    resource: meta.resource,
    filters: { permanent: composeFilters(q.schema, q.filter) },
    sorters: { initial: q.sort ?? [] },
    pagination: clientMode
      ? { mode: "off" }
      : {
          currentPage: q.page?.index ?? 1,
          pageSize:    q.page?.size ?? 50,
        },
    meta: select ? { select } : undefined,
    queryOptions: {
      enabled: q.enabled ?? true,
      // Client-mode tables fetch once and reuse — never auto-refetch on
      // window focus / mount of a sibling pane (e.g. opening the peek).
      ...(clientMode ? { refetchOnWindowFocus: false, staleTime: Infinity } : {}),
    },
    syncWithLocation: !clientMode,
  });
}

// ─── useRunRecordQuery ──────────────────────────────────────────────────────

/**
 * Single-record fetch. Used by view / peek pages to load the parent
 * record before sections render.
 */
export function useRunRecordQuery<T extends BaseRecord = BaseRecord>(q: RecordQuery<T>) {
  const meta = getSchemaMeta(q.schema);
  // `columns` (config-composed field objects) drive the SELECT directly from
  // their deps — supports cross-entity fields not present in the schema.
  // Otherwise fall back to key→schema derivation.
  const select = q.columns
    ? buildSelectString(
        // buildSelectString only reads `col.deps`; FieldDef carries it.
         
        q.columns as unknown as Parameters<typeof buildSelectString>[0],
        // Detail fetch: include `*` so EVERY scalar column is present, even ones
        // only read by custom sections (which don't contribute fields). Relation
        // embeds still come from the columns' deps. Lists don't use this path.
        ["*", ...(meta.permanentFields ?? [])],
      )
    : deriveSelect(q.schema, q.fields ?? [], q.also, []);
  return useOne<T>({
    resource: meta.resource,
    id: q.id,
    meta: select ? { select } : undefined,
    queryOptions: { enabled: q.enabled ?? true },
  });
}
